import { useMemo } from 'react';

// Observable10 color palette from D3 - Default palette (10 colors)
// https://d3js.org/d3-scale-chromatic/categorical#schemeObservable10
const OBSERVABLE10_COLORS = [
  '#4269d0', // Blue
  '#FFDD00', // Summer Yellow
  '#ff725c', // Coral
  '#6cc5b0', // Teal
  '#3ca951', // Green
  '#ff8ab7', // Pink
  '#a463f2', // Purple
  '#97bbf5', // Light Blue
  '#9c6b4e', // Brown
  '#9498a0', // Gray
];

// Set3 color palette from D3 (12 colors)
// https://d3js.org/d3-scale-chromatic/categorical#schemeSet3
const SET3_COLORS = [
  '#8dd3c7', // Teal
  '#ffffb3', // Light Yellow
  '#bebada', // Light Purple
  '#fb8072', // Salmon
  '#80b1d3', // Sky Blue
  '#fdb462', // Orange
  '#b3de69', // Light Green
  '#fccde5', // Pink
  '#d9d9d9', // Gray
  '#bc80bd', // Purple
  '#ccebc5', // Mint
  '#ffed6f', // Yellow
];

// Tableau10 color palette from D3 (10 colors)
// https://d3js.org/d3-scale-chromatic/categorical#schemeTableau10
const TABLEAU10_COLORS = [
  '#4e79a7', // Blue
  '#FFDD00', // Summer Yellow
  '#e15759', // Red
  '#76b7b2', // Teal
  '#59a14f', // Green
  '#f28e2c', // Orange
  '#af7aa1', // Purple
  '#ff9da7', // Pink
  '#9c755f', // Brown
  '#bab0ab', // Gray
];

// Company color palette - ordered for optimal visual contrast (11 colors)
const COMPANY_COLORS = [
  '#FFDD00', // Summer Yellow
  '#666666', // Medium Gray - 2
  '#FFE600', // Sunshine Yellow
  '#373630', // Dark Gray
  '#FFEB66', // Medium Yellow
  '#999999', // Medium Gray - 1
  '#FFF199', // Light Yellow - 1
  '#000000', // Black
  '#FFF8CC', // Light Yellow - 2
  '#EBEBEB', // Light Gray - 2
  '#F9F9F9', // Light Gray
];

// Determine which palette to use based on environment variable
// Options: 'tableau10' (default), 'observable10', 'set3', 'company'
const colorScheme = process.env.REACT_APP_COLOR_SCHEME || 'tableau10';
const BASE_COLORS =
  colorScheme === 'company'
    ? COMPANY_COLORS
    : colorScheme === 'tableau10'
    ? TABLEAU10_COLORS
    : colorScheme === 'set3'
    ? SET3_COLORS
    : OBSERVABLE10_COLORS;

// Generate additional colors if needed
function generateAdditionalColors(count: number): string[] {
  const colors: string[] = [];

  if (colorScheme === 'company') {
    // Mix of yellows and grays for company colors
    const yellowHue = 50;
    const grayHue = 0;

    for (let i = 0; i < count; i++) {
      if (i % 2 === 0) {
        const lightness = 85 - i * 3;
        const saturation = 100 - i * 5;
        colors.push(`hsl(${yellowHue}, ${Math.max(saturation, 40)}%, ${Math.max(lightness, 30)}%)`);
      } else {
        const lightness = 75 - i * 4;
        colors.push(`hsl(${grayHue}, 0%, ${Math.max(lightness, 20)}%)`);
      }
    }
  } else {
    // Generate shades based on the current base palette (Set3 or Tableau10)
    for (let i = 0; i < count; i++) {
      const baseColorIndex = i % BASE_COLORS.length;
      const baseColor = BASE_COLORS[baseColorIndex];
      // Darken/lighten the base color slightly for variations
      const factor = Math.floor(i / BASE_COLORS.length) + 1;
      const adjustment = factor * 15;
      colors.push(`color-mix(in srgb, ${baseColor} ${100 - adjustment}%, ${factor % 2 === 0 ? 'white' : 'black'})`);
    }
  }

  return colors;
}

// Select colors based on number of groups
function selectColors(groupCount: number): string[] {
  if (groupCount <= BASE_COLORS.length) {
    // Use the base palette colors
    return BASE_COLORS.slice(0, groupCount);
  } else {
    // Use all base colors + dynamically generated ones
    const additionalCount = groupCount - BASE_COLORS.length;
    return [...BASE_COLORS, ...generateAdditionalColors(additionalCount)];
  }
}

/**
 * Hook to generate consistent colors for groups across chart and table
 * @param groups - Array of group names
 * @returns Record mapping group names to their colors
 */
export function useGroupColors(groups: string[]): Record<string, string> {
  return useMemo(() => {
    const colors = selectColors(groups.length);
    const colorMap: Record<string, string> = {};
    groups.forEach((group, index) => {
      colorMap[group] = colors[index];
    });
    return colorMap;
  }, [groups]);
}
